/**
 * @file    ContactSection.js
 * @author  Daniel Naftzger
 * @created 2026-02-26
 * @updated 2026-02-26
 *
 * @description
 * Contact section that hides email, GitHub, and LinkedIn links behind a
 * Google reCAPTCHA v2 challenge. Contact info is not stored in the frontend
 * at all — it's fetched from the server only after the captcha is verified.
 *
 * Flow:
 *   1. User sees the reCAPTCHA checkbox (loaded from Google's script)
 *   2. User completes the challenge (no callback — React stays untouched)
 *   3. User clicks "Show Contact Info" button
 *   4. On click, we read the token with grecaptcha.getResponse() and POST
 *      it to the PHP backend for verification
 *   5. If valid, the backend responds with the contact info and we display it
 *
 * NOTE: We intentionally do NOT use reCAPTCHA's callback option. Triggering
 * React state updates from inside reCAPTCHA's callback causes a conflict —
 * React re-renders the DOM while reCAPTCHA's script is still animating,
 * which crashes with a null reference error. By using a button click instead,
 * we fully decouple the two.
 */
import { useState, useEffect, useRef } from 'react';
import styles from '../styles';

// ---------------------------------------------------------------------------
// REPLACE THIS with your actual Site Key from the reCAPTCHA admin console.
// This is the PUBLIC key — it's safe to have in frontend code.
// ---------------------------------------------------------------------------
const RECAPTCHA_SITE_KEY = '6LerEHksAAAAAAPcR0qyxKxf8gylJNQtYhoVe_2X';

/**
 * Loads the Google reCAPTCHA script tag into the page if it hasn't been
 * loaded already. Returns a promise that resolves once the script is ready.
 *
 * @returns {Promise<void>}
 */
function loadRecaptchaScript() {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.render) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;

    window.onRecaptchaLoad = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));

    document.head.appendChild(script);
  });
}

/**
 * Sends the captcha token to the PHP backend for verification.
 * If valid, returns the contact info object. If not, throws an error.
 *
 * @param {string} token - The reCAPTCHA response token from Google
 * @returns {Promise<{email: string, github: string, linkedin: string}>}
 */
async function verifyToken(token) {
  const response = await fetch('/verify-recaptcha.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Verification failed');
  }

  return data;
}

/**
 * Renders the contact section. Shows a reCAPTCHA widget and a submit button.
 * After the user passes the challenge and clicks the button, reveals the
 * contact links.
 *
 * @returns {JSX.Element}
 */
function ContactSection() {
  /** @type {[null|{email:string, github:string, linkedin:string}, Function]} */
  const [contactInfo, setContactInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef(null);
  const widgetRendered = useRef(false);

  /**
   * On mount, loads the reCAPTCHA script and renders the checkbox widget
   * inside the captchaRef div. No callback is registered — the widget just
   * sits there until the user clicks our button.
   */
  useEffect(() => {
    loadRecaptchaScript()
      .then(() => {
        if (captchaRef.current && !widgetRendered.current) {
          window.grecaptcha.render(captchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
          });
          widgetRendered.current = true;
        }
      })
      .catch(() => setError('Could not load captcha. Please refresh the page.'));
  }, []);

  /**
   * Called when the user clicks the "Show Contact Info" button.
   * Reads the captcha token (if the user completed the challenge),
   * sends it to the backend, and displays the contact info on success.
   */
  async function handleSubmit() {
    const token = window.grecaptcha.getResponse();

    if (!token) {
      setError('Please complete the captcha first.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await verifyToken(token);
      setContactInfo(data);
    } catch (err) {
      setError('Verification failed. Please try again.');
      window.grecaptcha.reset();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="dn-contact-box" style={styles.contactBox}>
      <p style={styles.contactIntro}>
        Have a project in mind or want to chat? I'd love to hear from you.
      </p>

      {contactInfo && (
        <>
          <a href={`mailto:${contactInfo.email}`} style={styles.contactEmail}>
            {contactInfo.email}
          </a>
          <div style={styles.contactLinks}>
            <a href={contactInfo.github} target="_blank" rel="noreferrer" style={styles.contactLink}>
              GitHub ↗
            </a>
            <a href={contactInfo.linkedin} target="_blank" rel="noreferrer" style={styles.contactLink}>
              LinkedIn ↗
            </a>
          </div>
        </>
      )}

      {/*
        The captcha wrapper stays in the DOM always. Hiding via display:none
        instead of unmounting prevents reCAPTCHA's script from crashing
        when it tries to reference its own DOM elements after verification.
      */}
      <div style={{ display: contactInfo ? 'none' : 'block' }}>
        <p style={{ color: '#888', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Complete the captcha below to see my contact info.
        </p>

        <div ref={captchaRef} style={{ marginBottom: '1rem' }}></div>

        <button onClick={handleSubmit} style={styles.btnPrimary}>
          Show Contact Info
        </button>

        {loading && (
          <p style={{ color: '#e8ff47', fontSize: '0.85rem', marginTop: '0.75rem' }}>Verifying...</p>
        )}
        {error && (
          <p style={{ color: '#ff6b6b', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</p>
        )}
      </div>
    </div>
  );
}

export default ContactSection;